import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { hybridCardService } from './services/hybridCardService';
import { pokemonService } from './services/pokemonService';

import { socialService, NotificationItem } from './services/socialService';
import { systemService } from './services/systemService';
import { collectionService, WishlistItem } from './services/collectionService';
import { PokemonCard, Deck, GoogleUser, SocialProfile } from './types';
import { auth } from './services/firebase'; // Import Firebase Auth
import { GoogleAuthProvider, signInWithCredential, onAuthStateChanged, signOut } from 'firebase/auth';
import PokemonCardItem from './components/PokemonCardItem';
import CardDetailsModal from './components/CardDetailsModal';
import LandingPage from './components/LandingPage';
import FilterBar, { FilterState } from './components/FilterBar';
import BottomNav from './components/BottomNav';

// --- LAZY LOAD HEAVY COMPONENTS (Performance Optimization) ---
const CameraScanner = lazy(() => import('./components/CameraScanner'));
const SubscriptionModal = lazy(() => import('./components/SubscriptionModal'));
const SocialHub = lazy(() => import('./components/SocialHub'));
const MessageCenter = lazy(() => import('./components/MessageCenter'));
const AuctionHouse = lazy(() => import('./components/AuctionHouse'));
const ChatWindow = lazy(() => import('./components/ChatWindow'));
const OnboardingModal = lazy(() => import('./components/OnboardingModal'));
const ChangelogModal = lazy(() => import('./components/ChangelogModal'));
const MissionModal = lazy(() => import('./components/MissionModal'));

// Type imports need to be explicit or moved to types.ts to avoid build errors 
// if they were re-exported from the component file. 
// For now, we assume ScannedItem is only used as a type and TS handles it.
import type { ScannedItem } from './components/CameraScanner';
import { SubscriptionTier } from './components/SubscriptionModal'; // Enums/Values need to stay if used as value

import {
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Library,
  Heart,
  Compass,
  LayoutGrid,
  Trash2,
  X,
  Scan,
  ShieldCheck,
  UserCheck,
  Eye,
  EyeOff,
  DollarSign,
  Users,
  Lock,
  Unlock,
  HelpCircle,
  FileText,
  PlayCircle,
  Plus,
  CheckCircle,
  Cpu,
  Bell,
  MessageSquare,
  Zap,
  CreditCard,
  Download,
  Upload,
  Gavel,
  Star,
  ShieldAlert,
  LogOut,
  Edit2
} from 'lucide-react';

type Tab = 'explore' | 'favorites' | 'decks' | 'social' | 'messages' | 'auctions';

// Enhanced GoogleUser type for local state
interface AppUser extends GoogleUser {
  isPublic: boolean;
}

const TAB_ORDER: Tab[] = ['explore', 'social', 'messages'];

const App: React.FC = () => {
  // Real Google Authentication State
  const [user, setUser] = useState<AppUser | null>(null);
  const isAuthenticated = !!user;

  // App Initialization State (Loading Screen)
  const [isAppReady, setIsAppReady] = useState(false);

  // System Notifications State
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showMission, setShowMission] = useState(false);

  // Global Feedback State
  const [globalToast, setGlobalToast] = useState<{ message: string, type: 'success' | 'error' | 'info' } | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);

  // API Status State
  const [isApiOnline, setIsApiOnline] = useState(true);
  const [showApiStatus, setShowApiStatus] = useState(false);

  // Notification / Alert State
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState<NotificationItem | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);

  // Subscription State (Multi-Tier)
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');
  const [showPaywall, setShowPaywall] = useState(false);

  // Derived permission checks
  const isPro = subscriptionTier !== 'free';
  const isGiovanni = subscriptionTier === 'giovanni' || subscriptionTier === 'sigma';

  // Landing Page State
  const [showLanding, setShowLanding] = useState(true);

  // Main UI State
  const [activeTab, setActiveTab] = useState<Tab>('explore');
  const [cards, setCards] = useState<PokemonCard[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [vaultSearchQuery, setVaultSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filtering State
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    minPrice: '',
    maxPrice: '',
    year: '',
    rarity: '',
    sort: 'price-desc'
  });

  // Chat State (Direct Access)
  const [activeChatRecipient, setActiveChatRecipient] = useState<SocialProfile | null>(null);

  // Persistence Refs
  const lastFetchedParams = useRef({ query: '__INITIAL_BOOT_SEQUENCE__', page: 0 });
  const isScanningRef = useRef(false); // Prevents auto-fetch race condition during scan
  const googleButtonRef = useRef<HTMLDivElement>(null); // Ref for Google Button (Desktop)
  const googleButtonRefMobile = useRef<HTMLDivElement>(null); // Ref for Google Button (Mobile)
  const fileImportRef = useRef<HTMLInputElement>(null); // Hidden input for file import

  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);

  // Navigation State
  const [selectedCard, setSelectedCard] = useState<PokemonCard | null>(null);
  const [viewContext, setViewContext] = useState<PokemonCard[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);

  // Swipe Navigation State
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Deck Creation State
  const [showCreateDeckModal, setShowCreateDeckModal] = useState(false);
  const [editingDeckId, setEditingDeckId] = useState<string | null>(null);
  const [currentDeckId, setCurrentDeckId] = useState<string | null>(null);
  const [newDeckName, setNewDeckName] = useState('');

  // Identity-Linked State
  const [favorites, setFavorites] = useState<PokemonCard[]>([]);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);

  // Wishlist Match Alerts (cards you have that others want)
  const [wishlistMatches] = useState<{ card: PokemonCard, wantedBy: { userId: string, userName?: string }[] }[]>([]);

  // Vault State
  const [hideBalance, setHideBalance] = useState(false);

  // Helper to extract a usable market price for a card
  const getCardPrice = (card: PokemonCard): number => {
    const prices = card.tcgplayer?.prices;
    if (!prices) return 0;
    // Priorities: Holofoil > Normal > Reverse > 1stEd
    return prices.holofoil?.market ||
      prices.normal?.market ||
      prices.reverseHolofoil?.market ||
      prices['1stEditionHolofoil']?.market || 0;
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setGlobalToast({ message, type });
    setTimeout(() => setGlobalToast(null), 4000);
  };

  // Sound Player
  const playNotificationSound = () => {
    try {
      // Better approach: Simple AudioContext beep if file fails
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880; // High pitch notification
      gain.gain.value = 0.1;
      osc.start();
      setTimeout(() => {
        osc.stop();
        // Double beep
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.frequency.value = 1200;
          gain2.gain.value = 0.1;
          osc2.start();
          setTimeout(() => osc2.stop(), 100);
        }, 150);
      }, 100);

    } catch (e) {
      console.warn("Audio playback blocked", e);
    }
  };

  // Subscribe to Notifications when User Logs In
  useEffect(() => {
    if (user) {
      // Request browser notification permission on login
      socialService.requestNotificationPermission().then(granted => {
        if (granted) console.log('[Notifications] Browser notifications enabled');
      });

      const unsubscribe = socialService.subscribeToNotifications(user.id, (count, latest) => {
        setUnreadCount(count);
        if (latest) {
          setLatestNotification(latest);
          showToast(`New Message from ${latest.title}`, 'info');
          playNotificationSound();
        }
      });
      return () => unsubscribe();
    }
  }, [user]);

  // Handle Notification Click (Smart Relay)
  const handleNotificationClick = async (notif: NotificationItem) => {
    setShowNotifications(false);

    if (notif.type === 'message' && notif.senderId) {
      // 1. Switch to messages tab
      setActiveTab('messages');

      // 2. Fetch the user profile to open chat directly
      try {
        setProcessingStatus("Retrieving Secure Channel...");
        const senderProfile = await socialService.getUserById(notif.senderId);
        if (senderProfile) {
          setActiveChatRecipient(senderProfile);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setProcessingStatus(null);
      }
    } else {
      // Default fallbacks
      if (notif.type === 'message') setActiveTab('messages');
      else setActiveTab('social');
    }
  };

  // Initialize System
  useEffect(() => {
    async function initSystem() {
      console.log("[System] Boot Sequence Initiated...");
      const MIN_LOADING_TIME = 800; // Quick splash - just enough for visual feedback
      const startTime = Date.now();

      // 1. Restore Auth (Local Storage Fallback or Firebase Listener)
      const savedUserStr = localStorage.getItem('pokebase_google_user');
      const savedTier = localStorage.getItem('pokebase_subscription_tier') as SubscriptionTier | null;

      // Set initial state from local storage to prevent flicker
      if (savedUserStr && !auth) { // Only use LS if auth isn't ready or configured
        const parsedUser = JSON.parse(savedUserStr);
        if (typeof parsedUser.isPublic === 'undefined') parsedUser.isPublic = true;
        setUser(parsedUser);
      }
      if (savedTier && ['pro', 'giovanni', 'sigma'].includes(savedTier)) {
        setSubscriptionTier(savedTier);
      }

      // 2. Check System Updates / Onboarding
      if (systemService.shouldShowOnboarding()) {
        setShowOnboarding(true);
      } else if (systemService.shouldShowChangelog()) {
        setShowChangelog(true);
      }

      // 3. Use fallback ticker data immediately (no API wait)
      // Ticker cards already initialized with FALLBACK_TICKER_CARDS
      // Optionally fetch fresh data in background after app is ready

      // 4. Check for Payment Success Return
      const params = new URLSearchParams(window.location.search);
      if (params.get('success') === 'true') {
        const tier = (params.get('tier') as SubscriptionTier) || 'pro';
        setSubscriptionTier(tier);
        localStorage.setItem('pokebase_subscription_tier', tier);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Brief splash for visual polish (non-blocking)
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_LOADING_TIME - elapsed);
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      // 5. Complete Boot
      setIsAppReady(true);

      // 6. Init Google Auth Lib
      if ((window as unknown as { google: any }).google) {
        try {
          (window as unknown as { google: any }).google.accounts.id.initialize({
            client_id: "24343609456-asrjjkrn3jjk1r7ng8lnddk8nukgr8ub.apps.googleusercontent.com",
            use_fedcm_for_prompt: false,
            callback: async (response: any) => {
              // BRIDGE: Google Identity -> Firebase Auth
              if (auth) {
                try {
                  const credential = GoogleAuthProvider.credential(response.credential);
                  await signInWithCredential(auth, credential);
                  // The onAuthStateChanged listener below will handle the setUser
                } catch (error: unknown) {
                  console.error("Firebase Sign In Error", error);
                  showToast("Secure Link Failed: " + (error instanceof Error ? error.message : String(error)), "error");
                }
              } else {
                // Fallback: No Firebase Configured, use Manual JWT
                try {
                  const base64Url = response.credential.split('.')[1];
                  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                  }).join(''));
                  const payload = JSON.parse(jsonPayload);

                  const googleUser: AppUser = {
                    id: payload.sub,
                    fullName: payload.name,
                    email: payload.email,
                    avatar: payload.picture,
                    isPublic: true
                  };
                  setUser(googleUser);
                  localStorage.setItem('pokebase_google_user', JSON.stringify(googleUser));
                } catch (e: unknown) {
                  console.error("Identity Parsing Error", e);
                  showToast("Identity Parsing Error", "error");
                }
              }
            }
          });
        } catch (e: unknown) {
          console.error("Auth Init Failed", e);
          showToast("Auth Init Failed", "error");
        }
      }
    }

    initSystem();
  }, []);

  // Firebase Auth Listener
  useEffect(() => {
    if (auth) {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          // Start with Firebase defaults
          const appUser: AppUser = {
            id: firebaseUser.uid,
            fullName: firebaseUser.displayName || 'Unknown Agent',
            email: firebaseUser.email || '',
            avatar: firebaseUser.photoURL || '',
            isPublic: true // Default
          };

          // CRITICAL: Merge with local preferences to restore ALL custom fields
          const saved = localStorage.getItem('pokebase_google_user');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.id === appUser.id) {
              // Restore ALL customized fields (not just some)
              appUser.isPublic = parsed.isPublic ?? true;
              appUser.fullName = parsed.fullName || appUser.fullName; // Custom Name
              appUser.avatar = parsed.avatar || appUser.avatar;       // Custom Avatar
              appUser.bannerUrl = parsed.bannerUrl;
              appUser.tagline = parsed.tagline;
              appUser.subscriptionTier = parsed.subscriptionTier; // Restore local tier first
            }
          }

          // ASYNC SYNC: Fetch latest profile from Firestore to get true subscription status
          socialService.getUserById(appUser.id).then((profile) => {
            if (profile && profile.subscriptionTier) {
              console.log("Synced Subscription from Cloud:", profile.subscriptionTier);
              setSubscriptionTier(profile.subscriptionTier);
              appUser.subscriptionTier = profile.subscriptionTier;

              // Update state and local storage with cloud truth
              setUser((prev) => prev ? { ...prev, subscriptionTier: profile.subscriptionTier } : appUser);
              localStorage.setItem('pokebase_google_user', JSON.stringify(appUser));
              localStorage.setItem('pokebase_subscription_tier', profile.subscriptionTier!);
            }
          }).catch(err => console.error("Profile Sync Error:", err));

          setUser(appUser);
          localStorage.setItem('pokebase_google_user', JSON.stringify(appUser));
        } else {
          // User signed out
          setUser(null);
          localStorage.removeItem('pokebase_google_user');
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // Clean up Google One Tap/Button if user is logged in
  useEffect(() => {
    if (user && (window as unknown as { google: any }).google) {
      try {
        // Force close any "Sign in as..." prompts if the user is already authenticated
        (window as unknown as { google: any }).google.accounts.id.cancel();
      } catch (e) {
        console.warn("Could not cancel Google prompt", e);
      }
    }
  }, [user]);

  // Render Google Button when Ready and User Not Logged In
  useEffect(() => {
    if (isAppReady && !user && (window as unknown as { google: any }).google) {
      try {
        // Render desktop button
        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = '';
          (window as unknown as { google: any }).google.accounts.id.renderButton(
            googleButtonRef.current,
            {
              theme: 'filled_black',
              size: 'large',
              shape: 'pill',
              text: 'signin_with',
              logo_alignment: 'left'
            }
          );
        }
        if (googleButtonRefMobile.current) {
          googleButtonRefMobile.current.innerHTML = '';
          (window as unknown as { google: any }).google.accounts.id.renderButton(
            googleButtonRefMobile.current,
            {
              theme: 'filled_black',
              size: 'large',
              shape: 'pill',
              text: 'signin_with',
              logo_alignment: 'left',
              width: '100%'
            }
          );
        }
      } catch (e: unknown) {
        console.error("Button Render Failed", e);
      }
    }
  }, [isAppReady, user, showProfileMenu]);

  const handleSignOut = async () => {
    if (auth) {
      await signOut(auth);
    }
    localStorage.removeItem('pokebase_google_user');
    setActiveTab('explore');
    // setAuthError(null);
    setShowProfileMenu(false);
    // Google button will re-render due to Effect
  };

  const handleUpdateProfile = (updatedData: Partial<GoogleUser>) => {
    if (!user) return;
    const newUser = { ...user, ...updatedData };
    setUser(newUser);
    localStorage.setItem('pokebase_google_user', JSON.stringify(newUser));
    showToast("Profile Identity Updated", "success");

    // Also sync to social service
    socialService.syncUserProfile(newUser, favorites);
  };

  const togglePrivacy = () => {
    if (!user) return;
    const updatedUser = { ...user, isPublic: !user.isPublic };
    setUser(updatedUser);
    localStorage.setItem('pokebase_google_user', JSON.stringify(updatedUser));
  };

  const handleSubscribe = async (tier: SubscriptionTier) => {
    // await socialService.subscribeToPro(); // Removed mock delay
    setSubscriptionTier(tier);
    localStorage.setItem('pokebase_subscription_tier', tier);

    // Sync to user profile for persistence
    if (user) {
      const updatedUser = { ...user, subscriptionTier: tier };
      setUser(updatedUser);
      localStorage.setItem('pokebase_google_user', JSON.stringify(updatedUser));
      socialService.syncUserProfile(updatedUser, favorites);
    }

    setShowPaywall(false);
    showToast(`${tier.charAt(0).toUpperCase() + tier.slice(1)} tier activated!`, "success");
  };

  const handleCancelSubscription = () => {
    setSubscriptionTier('free');
    localStorage.removeItem('pokebase_subscription_tier');
    showToast("Subscription Canceled", "info");
  };

  const handleFinishOnboarding = () => {
    setShowOnboarding(false);
    systemService.completeOnboarding();
  };

  const handleCloseChangelog = () => {
    setShowChangelog(false);
    systemService.ackChangelog();
  };

  // --- DATA IMPORT / EXPORT LOGIC ---

  const handleExportData = () => {
    try {
      const exportData = {
        version: "1.0",
        timestamp: Date.now(),
        sourceUser: user?.fullName || 'Guest',
        vault: favorites,
        decks: decks
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pokebase_collection_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast("Collection Data Exported", "success");
      setShowProfileMenu(false);
      showToast("Collection Data Exported", "success");
      setShowProfileMenu(false);
    } catch {
      showToast("Export Failed", "error");
    }
  };

  const triggerImport = () => {
    fileImportRef.current?.click();
  };

  const handleImportData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);

        // Basic Validation
        if (!json.vault || !Array.isArray(json.vault)) {
          throw new Error("Invalid file format");
        }

        // Merge Vault Logic (Combine Quantities)
        const newVault = [...favorites];
        let addedCards = 0;
        let updatedCards = 0;

        json.vault.forEach((importedCard: PokemonCard) => {
          const existingIndex = newVault.findIndex(c => c.id === importedCard.id);
          if (existingIndex >= 0) {
            // Update existing
            newVault[existingIndex].quantity = (newVault[existingIndex].quantity || 1) + (importedCard.quantity || 1);
            updatedCards++;
          } else {
            // Add new
            newVault.push(importedCard);
            addedCards++;
          }
        });

        // Merge Decks Logic (Append with new IDs to avoid conflicts)
        const newDecks = (json.decks || []).map((d: Deck) => ({
          ...d,
          id: Math.random().toString(36).substr(2, 9), // Regenerate ID
          name: `${d.name} (Imported)`
        }));
        const finalDecks = [...decks, ...newDecks];

        setFavorites(newVault);
        setDecks(finalDecks);

        showToast(`Import Success: ${addedCards} new cards, ${updatedCards} merged.`, "success");
        setShowProfileMenu(false);
      } catch (err) {
        console.error(err);
        showToast("Import Failed: Invalid or Corrupt File", "error");
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Load User-Specific Database (Cloud-First, LocalStorage Fallback)
  useEffect(() => {
    const userId = user?.id || 'guest';

    async function loadUserData() {
      // 1. Try to load from Firestore first (for logged-in users)
      if (user?.id) {
        try {
          const cloudData = await collectionService.loadCollection(user.id);
          if (cloudData && (cloudData.favorites.length > 0 || cloudData.decks.length > 0)) {
            console.log("[Sync] Loaded collection from cloud");
            setFavorites(cloudData.favorites);
            setDecks(cloudData.decks);
            setWishlist(cloudData.wishlist || []);
            return;
          } else {
            // CLOUD EMPTY? RECOVERY MODE: Check if we have LOCAL GUEST data to migrate
            console.log("[Sync] Cloud empty. Checking for local orphan data...");
            const guestFavs = localStorage.getItem('pokebase_favs_guest');
            const guestDecks = localStorage.getItem('pokebase_decks_guest');

            if (guestFavs || guestDecks) {
              const recoveredFavs = guestFavs ? JSON.parse(guestFavs) : [];
              const recoveredDecks = guestDecks ? JSON.parse(guestDecks) : [];
              setFavorites(recoveredFavs);
              setDecks(recoveredDecks);
              showToast("Restored local data to account", "info");
              // Auto-save to cloud to persist recovery
              collectionService.saveVault(user.id, recoveredFavs);
              collectionService.saveDecks(user.id, recoveredDecks);
              return;
            }
          }
        } catch (e) {
          console.warn("[Sync] Cloud load failed, using local", e);
        }
      }

      // 2. Fallback to localStorage
      const savedFavorites = localStorage.getItem(`pokebase_favs_${userId}`);
      const savedDecks = localStorage.getItem(`pokebase_decks_${userId}`);
      const savedWishlist = localStorage.getItem(`pokebase_wishlist_${userId}`);
      setFavorites(savedFavorites ? JSON.parse(savedFavorites) : []);
      setDecks(savedDecks ? JSON.parse(savedDecks) : []);
      setWishlist(savedWishlist ? JSON.parse(savedWishlist) : []);
    }

    loadUserData();
  }, [user?.id]);

  // Persist User-Specific Database (Cloud + Local)
  useEffect(() => {
    const userId = user?.id || 'guest';
    // Always save to localStorage as backup
    localStorage.setItem(`pokebase_favs_${userId}`, JSON.stringify(favorites));

    // Sync to Firestore if logged in (debounced)
    if (user?.id && favorites.length >= 0) {
      const timer = setTimeout(() => {
        collectionService.saveVault(user.id, favorites);
        console.log("[Sync] Vault synced to cloud");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [favorites, user?.id]);

  useEffect(() => {
    const userId = user?.id || 'guest';
    // Always save to localStorage as backup
    localStorage.setItem(`pokebase_decks_${userId}`, JSON.stringify(decks));

    // Sync to Firestore if logged in (debounced)
    if (user?.id && decks.length >= 0) {
      const timer = setTimeout(() => {
        collectionService.saveDecks(user.id, decks);
        console.log("[Sync] Decks synced to cloud");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [decks, user?.id]);

  // Persist Wishlist (Cloud + Local)
  useEffect(() => {
    const userId = user?.id || 'guest';
    localStorage.setItem(`pokebase_wishlist_${userId}`, JSON.stringify(wishlist));

    if (user?.id && wishlist.length >= 0) {
      const timer = setTimeout(() => {
        collectionService.saveWishlist(user.id, wishlist);
        console.log("[Sync] Wishlist synced to cloud");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [wishlist, user?.id]);

  // PERFORMANCE FIX: Disabled automatic trade matching to prevent network congestion
  // This was causing the "slow loading" issue by fetching all public profiles on startup
  /*
  useEffect(() => {
    if (!user?.id || favorites.length === 0) {
      setWishlistMatches([]);
      return;
    }

    const checkMatches = async () => {
      try {
        const matches = await collectionService.getWantedCardsMatches(favorites, user.id);
        setWishlistMatches(matches);
        if (matches.length > 0) {
          console.log(`[Social] ${matches.length} of your cards are wanted by others!`);
        }
      } catch (err: unknown) {
        console.warn("[Social] Wishlist match check failed:", err);
      }
    };

    // Debounce to avoid rapid checks
    const timer = setTimeout(checkMatches, 3000);
    return () => clearTimeout(timer);
  }, [favorites, user?.id]);
  */

  // SYNC USER TO SOCIAL REGISTRY (Cloud or Local)
  useEffect(() => {
    if (user) {
      // Debounce slightly to prevent spamming writes
      const timer = setTimeout(() => {
        socialService.syncUserProfile(user, favorites);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, favorites]);

  // === FILTERED VAULT LOGIC (Client-Side) ===
  const filteredFavorites = useMemo(() => {
    let result = [...favorites];

    // Filter by Text Search
    if (vaultSearchQuery.trim()) {
      const q = vaultSearchQuery.toLowerCase().trim();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.set.name.toLowerCase().includes(q) ||
        c.rarity?.toLowerCase().includes(q)
      );
    }

    // Filter by Price
    if (filters.minPrice) {
      result = result.filter(c => getCardPrice(c) >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      result = result.filter(c => getCardPrice(c) <= parseFloat(filters.maxPrice));
    }

    // Filter by Rarity
    if (filters.rarity) {
      result = result.filter(c => c.rarity?.toLowerCase() === filters.rarity.toLowerCase());
    }

    // Filter by Year
    if (filters.year) {
      result = result.filter(c => c.set.releaseDate?.startsWith(filters.year));
    }

    // Sort
    result.sort((a, b) => {
      if (filters.sort === 'price-desc') return getCardPrice(b) - getCardPrice(a);
      if (filters.sort === 'price-asc') return getCardPrice(a) - getCardPrice(b);
      if (filters.sort === 'date-desc') return new Date(b.set.releaseDate).getTime() - new Date(a.set.releaseDate).getTime();
      if (filters.sort === 'date-asc') return new Date(a.set.releaseDate).getTime() - new Date(b.set.releaseDate).getTime();
      return 0;
    });

    return result;
  }, [favorites, filters, vaultSearchQuery]);

  // === HYBRID FETCH LOGIC ===
  const fetchCards = useCallback(async (query: string, p: number, activeFilters?: FilterState) => {
    // SAFETY: Don't attempt to fetch API for local scan results
    if (query === '[Scanned Results]') return;

    // PERSISTENCE CHECK
    if (!activeFilters && cards.length > 0 && query === lastFetchedParams.current.query && p === lastFetchedParams.current.page) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let searchQuery = query;

      if (activeFilters) {
        if (activeFilters.rarity) searchQuery += ` rarity:"${activeFilters.rarity}"`;
        if (activeFilters.year) searchQuery += ` set.releaseDate:"${activeFilters.year}*"`;
      }

      // Use hybrid service - automatically handles API down scenario
      const result = await hybridCardService.searchCards(searchQuery, p, true);

      // Update API status based on result source
      if (result.source === 'api') {
        setIsApiOnline(true);
      } else if (result.source === 'local' || result.source === 'ai') {
        setIsApiOnline(false);
        setShowApiStatus(true);
      }

      // Post-fetch Sort
      let data = result.data;
      if (activeFilters && (activeFilters.sort.includes('price'))) {
        data.sort((a, b) => {
          const priceA = getCardPrice(a);
          const priceB = getCardPrice(b);
          return activeFilters.sort === 'price-desc' ? priceB - priceA : priceA - priceB;
        });
      }

      setCards(data);
      setTotalCount(result.totalCount);
      lastFetchedParams.current = { query: searchQuery, page: p };
    } catch (err: unknown) {
      console.error("[API Error]", err);
      setError(err instanceof Error ? err.message : 'Market Uplink Slow. Try again.');
    } finally {
      setLoading(false);
    }
  }, [cards.length]);

  // Monitor API Status
  useEffect(() => {
    const checkStatus = () => {
      const apiStatus = pokemonService.isApiAvailable();
      if (apiStatus !== isApiOnline) {
        setIsApiOnline(apiStatus);
        if (!apiStatus) {
          setShowApiStatus(true);
          showToast('Market API offline - using cached data', 'info');
        } else {
          setShowApiStatus(true);
          showToast('Market API restored', 'success');
          // Hide the banner after 3 seconds when restored
          setTimeout(() => setShowApiStatus(false), 3000);
        }
      }
    };

    // Check immediately and every 30 seconds
    checkStatus();
    const interval = setInterval(checkStatus, 30000);

    return () => clearInterval(interval);
  }, [isApiOnline]);

  // Initial Load (Explore) - Fetch cards on app ready
  useEffect(() => {
    if (activeTab === 'explore' && isAppReady) {
      // SCAN LOCK: Prevent auto-fetch if we are scanning
      if (isScanningRef.current) return;

      // Fetch if query changed OR on initial load (empty query fetches latest S&V cards)
      // NOTE: rely on handleSearch (Enter key) for explicit new searches to save API calls
      const queryToFetch = searchQuery || '';

      // Only auto-fetch on Page Change or Tab switch, NOT on every keystroke
      // We check if the INTENT is different from what we last fetched
      if (page !== lastFetchedParams.current.page || (queryToFetch !== lastFetchedParams.current.query && !lastFetchedParams.current.query.includes('FORCE_REFETCH'))) {
        // If query changed but page didn't, we usually wait for Enter. 
        // But for pagination/tab-switch with existing query, we proceed.
        if (queryToFetch === lastFetchedParams.current.query || page !== lastFetchedParams.current.page) {
          fetchCards(queryToFetch, page);
        }
      }

      // Fallback for initial boot
      if (lastFetchedParams.current.query === '__INITIAL_BOOT_SEQUENCE__') {
        fetchCards(queryToFetch, page);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeTab, isAppReady]); // REMOVED searchQuery to prevent API spam on typing
  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setPage(1);
    lastFetchedParams.current = { query: 'FORCE_REFETCH', page: 0 };
    // Pass current filters to the fetcher
    fetchCards(searchQuery, 1, filters);
  };

  const handleOpenCard = (card: PokemonCard, contextList: PokemonCard[], deckId?: string) => {
    const vaultInstance = favorites.find(f => f.id === card.id);
    setSelectedCard(vaultInstance || card);
    setViewContext(contextList);
    setCurrentDeckId(deckId || null);
  };

  const handleNavigateCard = (direction: 'next' | 'prev') => {
    if (!selectedCard || viewContext.length === 0) return;
    const currentIndex = viewContext.findIndex(c => c.id === selectedCard.id);
    if (currentIndex === -1) return;
    let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0) newIndex = viewContext.length - 1;
    if (newIndex >= viewContext.length) newIndex = 0;

    const nextCard = viewContext[newIndex];
    const vaultInstance = favorites.find(f => f.id === nextCard.id);
    setSelectedCard(vaultInstance || nextCard);
  };

  const handleOpenWishlistCard = async (clickedItemId: string) => {
    try {
      const ids = wishlist.map(w => w.cardId);
      if (ids.length === 0) return;

      // Batch fetch all wishlist cards for context (up to 250 for now)
      const query = ids.map(id => `id:"${id}"`).join(' OR ');
      const response = await pokemonService.searchCards(query, 1, 250);

      // Map back to preserve wishlist order
      const context = ids.map(id => response.data.find(c => c.id === id)).filter(Boolean) as PokemonCard[];

      const selectedCard = context.find(c => c.id === clickedItemId);
      if (selectedCard) {
        handleOpenCard(selectedCard, context);
      } else {
        // Fallback if not in batch for some reason
        const individual = await pokemonService.searchCards(`id:"${clickedItemId}"`, 1);
        if (individual.data.length > 0) {
          handleOpenCard(individual.data[0], []);
        }
      }
    } catch (e) {
      console.warn("Wishlist context fetch failed:", e);
    }
  };

  // UPDATED: Handle batch operations from Scanner
  const handleScanIdentify = async (
    results: ScannedItem[],
    action: { type: 'view' | 'vault' | 'deck', targetId?: string }
  ) => {
    setShowScanner(false);
    isScanningRef.current = true;
    setProcessingStatus(`Initiating Batch Protocol (Aggregating ${results.length} subjects)...`);

    try {
      // AGGREGATE QUANTITIES BEFORE PROCESSING
      const aggregatedResults: { [key: string]: { item: ScannedItem, qty: number } } = {};
      results.forEach(res => {
        const key = `${res.name}|${res.number || ''}|${res.set || ''}`;
        if (aggregatedResults[key]) {
          aggregatedResults[key].qty += 1;
        } else {
          aggregatedResults[key] = { item: res, qty: 1 };
        }
      });

      const itemsToProcess = Object.values(aggregatedResults);
      const foundCards: PokemonCard[] = [];
      const notFoundNames: string[] = [];

      for (let i = 0; i < itemsToProcess.length; i++) {
        const { item, qty } = itemsToProcess[i];
        setProcessingStatus(`Analyzing Subject ${i + 1}/${itemsToProcess.length}: ${item.name} (${qty}x)`);

        try {
          if (i > 0) await new Promise(r => setTimeout(r, 250));

          // Clean number (remove total count e.g. "4/102" -> "4")
          let cleanNumber = item.number;
          if (cleanNumber && cleanNumber.includes('/')) {
            cleanNumber = cleanNumber.split('/')[0];
          }

          // Attempt 1: Precise Search
          let query = `name:"${item.name}"`;
          if (cleanNumber) query += ` number:"${cleanNumber}"`;
          else if (item.number) query += ` number:"${item.number}"`;
          if (item.set) query += ` set.name:"${item.set}"`;

          let apiResult = await pokemonService.searchCards(query, 1);

          // Attempt 2: Loose Search (Name Only) via Hybrid Service
          // This activates the fuzzy matching, local DB, and AI fallback chain
          if (apiResult.data.length === 0) {
            console.log(`[Scan] Exact match failed for "${item.name}". Engaging hybrid loose search...`);
            const hybridResult = await hybridCardService.searchCards(item.name, 1);
            if (hybridResult.data.length > 0) {
              apiResult = hybridResult;
            }
          }

          if (apiResult.data.length > 0) {
            // Use the first result, but keep our scanned quantity
            // If the loose search returned multiple, the first one is usually the best match by name
            const bestMatch = apiResult.data[0];
            const card = { ...bestMatch, quantity: qty };
            foundCards.push(card);
          } else {
            notFoundNames.push(item.name);
          }
        } catch (e) {
          console.warn("Scan Item Fail", e);
          // Attempt 3: Emergency Fallback - if everything errors, try to just interpret the scan as valid? 
          // No, that risks corrupting the vault with fake data. Better to assume failure.
          notFoundNames.push(item.name);
        }
      }

      if (foundCards.length > 0) {
        if (action.type === 'view') {
          setCards(foundCards);
          setTotalCount(foundCards.length);
          setSearchQuery('[Scanned Results]');
          lastFetchedParams.current = { query: '[Scanned Results]', page: 1 };
          showToast(`${foundCards.length} Assets Loaded`, 'success');
        } else if (action.type === 'vault') {
          let addedTotal = 0;
          setFavorites(prev => {
            const next = [...prev];
            foundCards.forEach(card => {
              const idx = next.findIndex(c => c.id === card.id);
              if (idx >= 0) {
                next[idx] = {
                  ...next[idx],
                  quantity: (next[idx].quantity || 1) + (card.quantity || 1)
                };
              } else {
                next.push(card);
              }
              addedTotal += (card.quantity || 1);
            });
            return next;
          });
          setActiveTab('favorites');
          showToast(`${addedTotal} Units Secured in Vault`, 'success');
        } else if (action.type === 'deck' && action.targetId) {
          setDecks(prev => prev.map(d => {
            if (d.id === action.targetId) {
              const nextCards = [...d.cards, ...foundCards];
              return { ...d, cards: nextCards };
            }
            return d;
          }));
          setActiveTab('decks');
          showToast(`${foundCards.length} Asset Types Deployed to Formation`, 'success');
        }
      } else {
        showToast("Batch Analysis Failed: No matching database records found.", 'error');
      }
    } catch {
      showToast("Critical Uplink Failure during batch process.", 'error');
    } finally {
      setProcessingStatus(null);
      setTimeout(() => {
        isScanningRef.current = false;
      }, 500);
    }
  };

  const toggleFavorite = (card: PokemonCard) => {
    setFavorites(prev => {
      const idx = prev.findIndex(f => f.id === card.id);
      if (idx >= 0) {
        // If it's already a favorite, we might want to just toggle off 
        // OR increment if it's coming from a "Quick Add" scan.
        // For simplicity, standard toggle remains removal.
        return prev.filter(f => f.id !== card.id);
      }
      return [...prev, { ...card, quantity: card.quantity || 1, condition: 9 }];
    });
  };

  const toggleWishlist = (card: PokemonCard) => {
    setWishlist(prev => {
      const exists = prev.find(w => w.cardId === card.id);
      if (exists) return prev.filter(w => w.cardId !== card.id);
      return [...prev, {
        cardId: card.id,
        cardName: card.name,
        cardImage: card.images.small,
        addedAt: Date.now()
      }];
    });
  };

  const isOnWishlist = (cardId: string): boolean => {
    return wishlist.some(w => w.cardId === cardId);
  };

  const updateQuantity = (cardId: string, delta: number) => {
    setFavorites(prev => prev.map(c => {
      if (c.id === cardId) {
        const newQty = (c.quantity || 1) + delta;
        return { ...c, quantity: Math.max(1, newQty) };
      }
      return c;
    }));
    if (selectedCard?.id === cardId) {
      setSelectedCard(prev => prev ? { ...prev, quantity: Math.max(1, (prev.quantity || 1) + delta) } : null);
    }
  };

  const updateCondition = (cardId: string, condition: number) => {
    setFavorites(prev => prev.map(c => {
      if (c.id === cardId) {
        return { ...c, condition };
      }
      return c;
    }));
    if (selectedCard?.id === cardId) {
      setSelectedCard(prev => prev ? { ...prev, condition } : null);
    }
  };

  const addToDeck = (deckId: string, card: PokemonCard) => {
    setDecks(prev => prev.map(d => {
      if (d.id === deckId) return { ...d, cards: [...d.cards, card] };
      return d;
    }));
  };

  const createDeckAndAdd = (name: string, card: PokemonCard) => {
    const newDeck: Deck = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      cards: [card],
      createdAt: Date.now()
    };
    setDecks(prev => [newDeck, ...prev]);
  };

  const createDeck = (name: string) => {
    const newDeck: Deck = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      cards: [],
      createdAt: Date.now()
    };
    setDecks(prev => [newDeck, ...prev]);
    setShowCreateDeckModal(false);
    setNewDeckName('');
  };

  const removeDeck = (id: string) => {
    if (confirm('Purge formation data?')) {
      setDecks(prev => prev.filter(d => d.id !== id));
    }
  };

  const updateDeckName = (id: string, name: string) => {
    if (!name.trim()) return;
    setDecks(prev => prev.map(d => d.id === id ? { ...d, name } : d));
    setEditingDeckId(null);
    showToast("Formation Protocol Updated", "success");
  };

  const removeFromDeck = (deckId: string, cardId: string) => {
    setDecks(prev => prev.map(d => {
      if (d.id === deckId) {
        return { ...d, cards: d.cards.filter(c => c.id !== cardId) };
      }
      return d;
    }));
    showToast("Unit Removed from Formation", "info");

    // If we're viewing the card, we might want to update the context list too
    if (selectedCard?.id === cardId) {
      setViewContext(prev => prev.filter(c => c.id !== cardId));
      // Optionally navigate to next card if one exists
      if (viewContext.length > 1) {
        handleNavigateCard('next');
      } else {
        setSelectedCard(null);
      }
    }
  };


  const getConditionMultiplier = (rating: number) => {
    if (rating >= 10) return 1.5;
    if (rating >= 9) return 1.0;
    if (rating >= 7) return 0.85;
    if (rating >= 5) return 0.7;
    if (rating >= 3) return 0.5;
    return 0.25;
  };

  const getVaultValue = (list: PokemonCard[]) => {
    return list.reduce((sum, card) => {
      const marketPrice = getCardPrice(card);
      const qty = card.quantity || 1;
      const cond = card.condition || 9;
      const multiplier = getConditionMultiplier(cond);
      return sum + (marketPrice * qty * multiplier);
    }, 0);
  };

  const getDeckValue = (deck: Deck) => {
    return deck.cards.reduce((sum, card) => sum + getCardPrice(card), 0);
  };

  const totalPages = Math.ceil(totalCount / 20);

  // SWIPE NAVIGATION HANDLERS
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = TAB_ORDER.indexOf(activeTab);
      if (currentIndex !== -1) {
        if (isLeftSwipe && currentIndex < TAB_ORDER.length - 1) {
          setActiveTab(TAB_ORDER[currentIndex + 1]);
        }
        if (isRightSwipe && currentIndex > 0) {
          setActiveTab(TAB_ORDER[currentIndex - 1]);
        }
      }
    }
  };

  const handleSelectSet = (setName: string) => {
    setSelectedCard(null);
    setSearchQuery(`set.name:"${setName}"`);
    setActiveTab('explore');
    setShowLanding(false);
    setPage(1);
    lastFetchedParams.current = { query: 'FORCE_REFETCH', page: 0 };
    fetchCards(`set.name:"${setName}"`, 1, filters);
  };

  // === FAST LOADING SCREEN ===
  if (!isAppReady) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-teal-500/5"></div>
        <div className="relative z-10 text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-30 animate-pulse"></div>
            <Loader2 className="w-20 h-20 text-blue-500 animate-spin relative z-10" />
          </div>
          <h1 className="text-3xl font-black font-heading tracking-tighter text-white uppercase">PokéBase</h1>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-[0.3em] mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  if (showLanding) {
    return <LandingPage onEnter={() => setShowLanding(false)} />;
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 selection:bg-blue-500/30 flex flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >

      {/* GLOBAL PROCESSING OVERLAY */}
      {processingStatus && (
        <div className="fixed inset-0 z-[300] bg-slate-950/90 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300">
          <div className="w-full max-w-md p-8 text-center">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-slate-800 rounded-full"></div>
              <div className="absolute inset-0 border-t-4 border-blue-500 rounded-full animate-spin"></div>
              <Cpu className="absolute inset-0 m-auto w-10 h-10 text-blue-400 animate-pulse" />
            </div>
            <h3 className="text-2xl font-black font-heading text-white uppercase tracking-tighter mb-2">Tactical Processor</h3>
            <p className="text-blue-400 font-mono text-sm font-bold uppercase tracking-widest animate-pulse">{processingStatus}</p>
            <div className="mt-8 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-[progress_2s_ease-in-out_infinite] w-1/3 rounded-full relative"></div>
            </div>
            <style>{`@keyframes progress { 0% { left: -33%; } 100% { left: 100%; } }`}</style>
          </div>
        </div>
      )}

      {/* GLOBAL TOAST NOTIFICATION */}
      {globalToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[250] animate-in slide-in-from-top-4 fade-in duration-300 pointer-events-none">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border backdrop-blur-md ${globalToast.type === 'success' ? 'bg-emerald-500/90 border-emerald-400/50 text-white' : globalToast.type === 'info' ? 'bg-blue-600/90 border-blue-500/50 text-white' : 'bg-red-600/90 border-red-500/50 text-white'}`}>
            {globalToast.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {globalToast.type === 'error' && <ShieldAlert className="w-5 h-5" />}
            {globalToast.type === 'info' && <MessageSquare className="w-5 h-5" />}
            <span className="text-xs font-black uppercase tracking-widest">{globalToast.message}</span>
          </div>
        </div>
      )}

      {/* API STATUS BANNER */}
      {showApiStatus && !isApiOnline && (
        <div className="fixed top-0 left-0 right-0 z-[300] bg-amber-500/90 border-b border-amber-400 px-4 py-2 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-950">
            <ShieldAlert className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Market API Offline - Showing Cached Cards</span>
          </div>
        </div>
      )}

      {/* Mobile-Responsive Header - Tier-colored accents */}
      <header className={`sticky top-0 z-40 bg-slate-950/90 backdrop-blur-2xl border-b px-4 py-4 sm:px-8 transition-all duration-500 ${subscriptionTier === 'sigma' ? 'border-teal-500/30 shadow-[0_4px_30px_-10px_rgba(20,184,166,0.4)]' :
        subscriptionTier === 'giovanni' ? 'border-red-500/30 shadow-[0_4px_30px_-10px_rgba(239,68,68,0.4)]' :
          subscriptionTier === 'pro' ? 'border-yellow-500/30 shadow-[0_4px_30px_-10px_rgba(234,179,8,0.3)]' :
            'border-slate-900'
        }`}>
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-5 items-center justify-between">

          <div className="flex items-center gap-4 group cursor-pointer w-full lg:w-auto justify-center lg:justify-start" onClick={() => setActiveTab('explore')}>
            <div className="relative flex items-center justify-center p-1">
              <div className={`absolute inset-0 blur-[20px] opacity-20 group-hover:opacity-40 transition-opacity duration-500 ${subscriptionTier === 'sigma' ? 'bg-teal-500' :
                subscriptionTier === 'giovanni' ? 'bg-red-500' :
                  subscriptionTier === 'pro' ? 'bg-yellow-500' :
                    'bg-blue-500'
                }`}></div>
              <svg className="w-12 h-12 relative z-10 transform group-hover:-translate-y-1 transition-transform duration-500 ease-out" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 21C5 21 9 23 12 23C15 23 19 21 19 21" stroke="#334155" strokeWidth="2" strokeLinecap="round" className="group-hover:stroke-blue-500 transition-colors duration-500" />
                <path d="M12 17V23" stroke="#334155" strokeWidth="2" strokeLinecap="round" className="group-hover:stroke-blue-500 transition-colors duration-500" />
                <path d="M12 17C16.4183 17 20 13.4183 20 9C20 4.58172 16.4183 1 12 1C7.58172 1 4 4.58172 4 9C4 13.4183 7.58172 17 12 17Z" stroke="white" strokeWidth="2" className={`drop-shadow-[0_0_10px_rgba(59,130,246,0.5)] ${subscriptionTier === 'sigma' ? 'drop-shadow-[0_0_15px_rgba(20,184,166,0.6)]' :
                  subscriptionTier === 'giovanni' ? 'drop-shadow-[0_0_15px_rgba(239,68,68,0.6)]' :
                    subscriptionTier === 'pro' ? 'drop-shadow-[0_0_15px_rgba(234,179,8,0.6)]' : ''
                  }`} />
                <path d="M4 9H8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <path d="M16 9H20" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="9" r="3" stroke="white" strokeWidth="2" className="fill-slate-950" />
                <circle cx="12" cy="9" r="1.5" className={`animate-pulse ${subscriptionTier === 'sigma' ? 'fill-teal-500' :
                  subscriptionTier === 'giovanni' ? 'fill-red-500' :
                    subscriptionTier === 'pro' ? 'fill-yellow-500' :
                      'fill-blue-500'
                  }`} />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black font-heading tracking-tighter text-white uppercase leading-none">POKÉBASE</h1>
                {subscriptionTier === 'pro' && <span className="px-2 py-0.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-slate-950 text-[10px] font-black uppercase tracking-widest rounded-md shadow-lg shadow-yellow-500/30 animate-pulse">⚡ PRO</span>}
                {subscriptionTier === 'giovanni' && <span className="px-2 py-0.5 bg-gradient-to-r from-red-600 to-red-500 text-white text-[10px] font-black uppercase tracking-widest rounded-md shadow-lg shadow-red-500/40">🔥 GIOVANNI</span>}
                {subscriptionTier === 'sigma' && <span className="px-2 py-0.5 bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 text-white text-[10px] font-black uppercase tracking-widest rounded-md shadow-lg shadow-teal-500/50 animate-pulse">👑 SIGMA</span>}
              </div>
              <p className={`text-[10px] uppercase font-bold tracking-[0.2em] mt-1 ${subscriptionTier === 'sigma' ? 'text-teal-400' :
                subscriptionTier === 'giovanni' ? 'text-red-400' :
                  subscriptionTier === 'pro' ? 'text-yellow-500/70' :
                    'text-slate-500'
                }`}>Tactical Analysis & Market Intel</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">

            {/* DESKTOP NAV - Hidden on Mobile */}
            <nav className="hidden md:flex flex-wrap justify-center bg-slate-900/50 p-1 rounded-2xl border border-slate-800 w-full md:w-auto overflow-x-auto">
              <button onClick={() => setActiveTab('explore')} className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'explore' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>
                <Compass className="w-4 h-4" /> Explore
              </button>
              <button
                onClick={() => { setActiveTab('social'); setUnreadCount(0); }}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'social' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <Users className="w-4 h-4" /> Network
              </button>
              <button
                onClick={() => { setActiveTab('messages'); setUnreadCount(0); }}
                className={`relative flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'messages' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <MessageSquare className="w-4 h-4" /> Messages
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[9px] text-white ring-2 ring-slate-950 animate-bounce">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button onClick={() => setActiveTab('favorites')} className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'favorites' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>
                <Heart className={`w-4 h-4 ${activeTab === 'favorites' ? 'fill-pink-500 text-pink-500' : ''}`} /> Vault
              </button>
              <button onClick={() => setActiveTab('decks')} className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'decks' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>
                <Library className="w-4 h-4" /> Decks
              </button>
              {isGiovanni && (
                <button onClick={() => setActiveTab('auctions')} className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'auctions' ? 'bg-red-500/20 text-red-400 shadow-lg' : 'text-red-500/50 hover:text-red-400'}`}>
                  <Gavel className="w-4 h-4" /> Auctions
                </button>
              )}
            </nav>

            {/* Desktop Help Button */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setShowHelpMenu(!showHelpMenu)}
                className="p-2.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                aria-label="Help Menu"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              {showHelpMenu && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden z-50 animate-in slide-in-from-top-2 fade-in duration-200">
                  <div className="p-1">
                    <button onClick={() => { setShowOnboarding(true); setShowHelpMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                      <PlayCircle className="w-4 h-4 text-cyan-500" /> System Tutorial
                    </button>
                    <button onClick={() => { setShowMission(true); setShowHelpMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                      <FileText className="w-4 h-4 text-blue-500" /> Mission Protocol
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 w-full md:w-auto justify-center md:justify-end">
              {isAuthenticated ? (
                <>
                  {/* NOTIFICATION BELL (Desktop Only - Mobile has it in Tab) */}
                  <div className="relative hidden md:block">
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="relative p-2.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                    >
                      <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'animate-pulse text-blue-400' : ''}`} />
                      {unreadCount > 0 && (
                        <span className="absolute top-0 right-0 h-2.5 w-2.5 bg-blue-500 rounded-full border border-slate-900"></span>
                      )}
                    </button>
                    {showNotifications && (
                      <div className="absolute top-full right-0 mt-2 w-72 bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden z-50 animate-in slide-in-from-top-2 fade-in duration-200">
                        <div className="p-3 border-b border-slate-800">
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Alerts & Messages</h4>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {unreadCount === 0 && !latestNotification ? (
                            <div className="p-6 text-center text-[10px] text-slate-600 font-bold uppercase">No active alerts.</div>
                          ) : (
                            <>
                              {latestNotification && (
                                <div onClick={() => handleNotificationClick(latestNotification)} className="p-4 hover:bg-slate-800 cursor-pointer border-b border-slate-800/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <MessageSquare className="w-3 h-3 text-blue-500" />
                                    <span className="text-xs font-bold text-white">{latestNotification.title}</span>
                                    <span className="text-[9px] text-blue-400 font-black bg-blue-500/10 px-1 rounded ml-auto">NEW</span>
                                  </div>
                                  <p className="text-[10px] text-slate-400 truncate">{latestNotification.body}</p>
                                </div>
                              )}
                              {/* Mock filler if unread > 1 */}
                              {unreadCount > 1 && (
                                <div className="p-3 text-center">
                                  <span className="text-[9px] text-blue-500 font-bold uppercase cursor-pointer hover:underline" onClick={() => { setActiveTab('messages'); setShowNotifications(false); }}>
                                    View {unreadCount - 1} more in Messages
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* USER MENU (Desktop - Dropdown / Mobile - Handled via Bottom Nav Trigger) */}
                  <div className="relative hidden md:block">
                    <button onClick={() => setShowProfileMenu(!showProfileMenu)} className={`flex items-center gap-3 bg-slate-900/80 pl-3 pr-4 py-1.5 rounded-full border hover:bg-slate-800 transition-all ${subscriptionTier === 'sigma' ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' :
                      subscriptionTier === 'giovanni' ? 'border-red-500/50 shadow-lg shadow-red-500/20' :
                        subscriptionTier === 'pro' ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' :
                          'border-slate-800'
                      }`}>
                      <div className="flex flex-col items-end mr-1">
                        <span className="text-[10px] font-black text-white leading-tight uppercase">{user.fullName}</span>
                        {user.isPublic ? (
                          <span className="text-[8px] font-bold text-green-500 uppercase tracking-widest flex items-center gap-1">Public <Unlock className="w-2 h-2" /></span>
                        ) : (
                          <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1">Private <Lock className="w-2 h-2" /></span>
                        )}
                      </div>
                      <div className="relative flex-shrink-0">
                        <img src={user.avatar} className={`w-8 h-8 rounded-full object-cover border-2 ${subscriptionTier === 'sigma' ? 'border-teal-500' :
                          subscriptionTier === 'giovanni' ? 'border-red-500' :
                            subscriptionTier === 'pro' ? 'border-yellow-500' :
                              'border-slate-700'
                          }`} referrerPolicy="no-referrer" alt="User Avatar" />
                        {subscriptionTier !== 'free' && (
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center text-[6px] ${subscriptionTier === 'sigma' ? 'bg-teal-500' :
                            subscriptionTier === 'giovanni' ? 'bg-red-500' :
                              'bg-yellow-500'
                            }`}>
                            {subscriptionTier === 'sigma' ? '👑' : subscriptionTier === 'giovanni' ? '🔥' : '⚡'}
                          </div>
                        )}
                      </div>
                      <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showProfileMenu && (
                      <div className="absolute top-full right-0 mt-2 w-52 bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden z-50 animate-in slide-in-from-top-2 fade-in duration-200">
                        <div className="p-1">
                          <button onClick={() => { setActiveTab('social'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            <UserCheck className="w-4 h-4" /> View Profile
                          </button>
                          <button onClick={() => { setActiveTab('favorites'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            <Heart className="w-4 h-4 text-pink-500" /> Access Vault
                          </button>
                          <button onClick={() => { setActiveTab('decks'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            <Library className="w-4 h-4 text-cyan-500" /> Manage Decks
                          </button>
                          <button onClick={() => { togglePrivacy(); }} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            {user.isPublic ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            {user.isPublic ? 'Make Private' : 'Make Public'}
                          </button>
                          <button onClick={() => { setShowPaywall(true); setShowProfileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-bold hover:text-white rounded-xl transition-colors text-left ${subscriptionTier === 'sigma' ? 'text-teal-400 hover:bg-teal-500/20' :
                            subscriptionTier === 'giovanni' ? 'text-red-400 hover:bg-red-500/20' :
                              subscriptionTier === 'pro' ? 'text-yellow-400 hover:bg-yellow-500/20' :
                                'text-slate-300 hover:bg-slate-800'
                            }`}>
                            {subscriptionTier === 'sigma' ? <span className="text-sm">👑</span> :
                              subscriptionTier === 'giovanni' ? <span className="text-sm">🔥</span> :
                                subscriptionTier === 'pro' ? <CreditCard className="w-4 h-4 text-yellow-500" /> :
                                  <Zap className="w-4 h-4 text-yellow-500" />}
                            {subscriptionTier === 'sigma' ? 'Sigma Status Active' :
                              subscriptionTier === 'giovanni' ? 'Giovanni Status Active' :
                                subscriptionTier === 'pro' ? 'Pro Status Active' :
                                  'Upgrade to Pro'}
                          </button>
                          <div className="h-px bg-slate-800 my-1 mx-2"></div>
                          {/* DATA MANAGEMENT BUTTONS */}
                          <button onClick={handleExportData} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            <Download className="w-4 h-4 text-blue-400" /> Export Data
                          </button>
                          <button onClick={triggerImport} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-left">
                            <Upload className="w-4 h-4 text-cyan-400" /> Import Data
                          </button>
                          <div className="h-px bg-slate-800 my-1 mx-2"></div>
                          <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors text-left">
                            <LogOut className="w-4 h-4" /> Log Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div ref={googleButtonRef} className="h-[44px] hidden md:block"></div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hidden File Input for Import */}
      <input
        type="file"
        ref={fileImportRef}
        onChange={handleImportData}
        accept=".json"
        className="hidden"
        aria-label="Import Collection Data"
      />

      {/* MOBILE PROFILE MENU (Bottom Sheet Style) */}
      {showProfileMenu && user && (
        <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm md:hidden flex flex-col justify-end" onClick={() => setShowProfileMenu(false)}>
          <div className="bg-slate-900 rounded-t-3xl border-t border-slate-800 p-6 animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-800">
              <img src={user.avatar} className="w-12 h-12 rounded-full object-cover border-2 border-slate-700" alt="Avatar" />
              <div>
                <h3 className="text-white font-black uppercase tracking-tight">{user.fullName}</h3>
                <p className="text-slate-500 text-xs font-bold">{user.email}</p>
              </div>
              {isPro && <span className="ml-auto bg-yellow-500 text-black px-2 py-1 rounded font-black text-[10px] uppercase">PRO</span>}
            </div>

            <div className="space-y-2">
              <button onClick={() => { setActiveTab('social'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <UserCheck className="w-5 h-5 text-blue-500" /> View My Profile
              </button>
              <button onClick={() => { setActiveTab('favorites'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <Heart className="w-5 h-5 text-pink-500" /> Access Vault
              </button>
              <button onClick={() => { setActiveTab('decks'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <Library className="w-5 h-5 text-cyan-500" /> Manage Decks
              </button>
              <button onClick={() => { togglePrivacy(); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                {user.isPublic ? <Lock className="w-5 h-5 text-green-500" /> : <Unlock className="w-5 h-5 text-slate-500" />}
                {user.isPublic ? 'Make Private' : 'Make Public'}
              </button>
              <button onClick={() => { setShowPaywall(true); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                {isPro ? <CreditCard className="w-5 h-5 text-green-500" /> : <Zap className="w-5 h-5 text-yellow-500" />}
                {isPro ? 'Manage Subscription' : 'Upgrade to Pro'}
              </button>
              <button onClick={handleExportData} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <Download className="w-5 h-5 text-blue-400" /> Export Data
              </button>
              <button onClick={triggerImport} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <Upload className="w-5 h-5 text-cyan-400" /> Import Data
              </button>
              <button onClick={() => { setShowMission(true); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 px-4 py-4 bg-slate-950/50 border border-slate-800 rounded-xl text-white font-bold uppercase text-xs">
                <FileText className="w-5 h-5 text-teal-400" /> Mission Protocol
              </button>
              <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-4 bg-red-900/10 border border-red-900/30 rounded-xl text-red-400 font-bold uppercase text-xs mt-4">
                <LogOut className="w-5 h-5" /> Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE LOGIN SHEET (When Not Authenticated) */}
      {showProfileMenu && !user && (
        <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm md:hidden flex flex-col justify-end" onClick={() => setShowProfileMenu(false)}>
          <div className="bg-slate-900 rounded-t-3xl border-t border-slate-800 p-6 animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <h3 className="text-white font-black uppercase tracking-tight text-xl mb-2">Welcome to PokéBase</h3>
              <p className="text-slate-400 text-sm">Sign in to access your collection, decks, and more</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <div ref={googleButtonRefMobile} className="w-full flex justify-center"></div>
              <button
                onClick={() => setShowProfileMenu(false)}
                className="text-slate-500 text-xs font-bold uppercase"
              >
                Continue Without Signing In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with bottom padding for Nav Bar */}
      <main className="flex-1 max-w-7xl mx-auto w-full p-4 sm:p-8 relative pb-24 md:pb-8">
        {activeTab === 'explore' && (
          <div className="space-y-6">
            <div className="relative group z-10">
              <form onSubmit={handleSearch} className="relative flex items-center bg-slate-900 border border-slate-800 rounded-2xl p-2 shadow-xl focus-within:border-blue-500/50 transition-colors">
                <Search className="w-5 h-5 text-slate-500 ml-3" />
                <input aria-label="Search Cards"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cards, sets, or series... (e.g. 'Charizard', 'Fusion Strike')"
                  className="flex-1 bg-transparent border-none text-white placeholder:text-slate-600 focus:ring-0 px-4 py-2 font-medium"
                />
                {/* Visible Search Submit Button */}
                <button
                  type="submit"
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-colors text-xs font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 shrink-0"
                >
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </button>
                {/* Desktop Scan Button (Mobile uses Bottom Bar) */}
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="hidden md:flex p-2 bg-slate-800 hover:bg-slate-700 text-blue-400 rounded-xl transition-colors items-center gap-2 ml-1"
                >
                  <Scan className="w-5 h-5" />
                  <span className="text-xs font-black uppercase tracking-widest">Scan</span>
                </button>
              </form>
              {/* Filter Toggle - OUTSIDE the form to prevent accidental search submission */}
              <div className="flex items-center gap-2 mt-2">
                <FilterBar
                  isOpen={showFilters}
                  onToggle={() => setShowFilters(!showFilters)}
                  filters={filters}
                  onChange={setFilters}
                  onApply={() => handleSearch()}
                  mode="explore"
                />
              </div>
            </div>

            {/* Collapsible Filter Panel (Expanded) */}
            {showFilters && (
              <div className="mt-[-20px] pt-6"> {/* Negative margin to tuck under search bar visually */}
                <FilterBar
                  isOpen={true}
                  onToggle={() => setShowFilters(false)}
                  filters={filters}
                  onChange={setFilters}
                  onApply={() => handleSearch()}
                  mode="explore"
                />
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-bold flex items-center gap-3 animate-pulse">
                <ShieldAlert className="w-5 h-5" />
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                <p className="text-slate-500 text-xs font-black uppercase tracking-widest animate-pulse">Querying Database...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
                  {cards.length > 0 ? (
                    cards.map(card => (
                      <PokemonCardItem
                        key={card.id}
                        card={card}
                        onClick={() => handleOpenCard(card, cards)}
                        onQuickAdd={toggleFavorite}
                        isFavorite={favorites.some(f => f.id === card.id)}
                      />
                    ))
                  ) : (
                    // EMPTY STATE -> SHOW READY TO EXPLORE PROMPT (Instant Load)
                    <div className="col-span-full text-center py-16">
                      <div className="max-w-md mx-auto">
                        <div className="relative w-24 h-24 mx-auto mb-6">
                          <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
                          <Search className="w-24 h-24 text-slate-700 relative z-10" />
                        </div>
                        <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2">Ready to Explore</h3>
                        <p className="text-slate-500 font-medium mb-6">Search for any Pokémon card by name, set, or type to begin your hunt.</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {['Charizard', 'Pikachu', 'Mewtwo', 'Umbreon'].map(term => (
                            <button
                              key={term}
                              onClick={() => {
                                setSearchQuery(term);
                                setPage(1);
                                lastFetchedParams.current = { query: 'FORCE_REFETCH', page: 0 };
                                fetchCards(term, 1, filters);
                              }}
                              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-full text-xs font-bold uppercase tracking-widest transition-all border border-slate-700 hover:border-slate-600"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {cards.length > 0 && (
                  <div className="flex justify-center gap-4 mt-8">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-3 bg-slate-800 rounded-xl disabled:opacity-50 text-white hover:bg-slate-700"
                      aria-label="Previous Page"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="flex items-center px-4 bg-slate-900 rounded-xl text-xs font-bold text-slate-400 border border-slate-800">
                      PAGE {page} / {totalPages || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages}
                      className="p-3 bg-slate-800 rounded-xl disabled:opacity-50 text-white hover:bg-slate-700"
                      aria-label="Next Page"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {cards.length === 0 && !loading && !error && searchQuery && (
                  <div className="text-center py-12 opacity-50">
                    <LayoutGrid className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                    <p className="text-slate-500 font-bold">No assets found matching "{searchQuery}"</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Vault Storage</h2>
              <div className="flex items-center gap-4 ml-auto">
                <button
                  onClick={() => setHideBalance(!hideBalance)}
                  className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors"
                >
                  {hideBalance ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  <span className="text-xs font-bold uppercase tracking-widest">{hideBalance ? 'Hidden' : 'Visible'}</span>
                </button>
                <div className="bg-slate-900 px-4 py-2 rounded-xl border border-slate-800 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span className="font-mono font-black text-white text-lg">
                    {hideBalance ? '****' : getVaultValue(filteredFavorites).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* NEW: Search Bar & Filter Integration */}
            <div className="relative group z-10">
              <div className="relative flex items-center bg-slate-900 border border-slate-800 rounded-2xl p-2 shadow-xl focus-within:border-blue-500/50 transition-colors">
                <Search className="w-5 h-5 text-slate-500 ml-3" />
                <input aria-label="Search Collection"
                  type="text"
                  value={vaultSearchQuery}
                  onChange={(e) => setVaultSearchQuery(e.target.value)}
                  placeholder="Search your collection..."
                  className="flex-1 bg-transparent border-none text-white placeholder:text-slate-600 focus:ring-0 px-4 py-2 font-medium"
                />
                {/* Filter Toggle Button */}
                <div className="pr-2">
                  <FilterBar
                    isOpen={showFilters}
                    onToggle={() => setShowFilters(!showFilters)}
                    filters={filters}
                    onChange={setFilters}
                    onApply={() => { }}
                    mode="vault"
                  />
                </div>
              </div>
            </div>

            {/* Collapsible Filter Panel (Expanded) */}
            {showFilters && (
              <div className="mt-[-20px] pt-6">
                <FilterBar
                  isOpen={true}
                  onToggle={() => setShowFilters(false)}
                  filters={filters}
                  onChange={setFilters}
                  onApply={() => { }}
                  mode="vault"
                />
              </div>
            )}

            {favorites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-slate-800 rounded-3xl">
                <ShieldCheck className="w-20 h-20 text-slate-800 mb-6" />
                <h3 className="text-xl font-bold text-slate-700 uppercase">Vault Empty</h3>
                <p className="text-slate-500 mt-2 text-sm">Add assets from Explore or Scan to track value.</p>
                <button onClick={() => setActiveTab('explore')} className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-blue-500">
                  Find Assets
                </button>
              </div>
            ) : (
              <>
                {filteredFavorites.length === 0 ? (
                  <div className="text-center py-10 opacity-50">
                    <p className="text-slate-500 font-bold">No vault items match filters.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
                    {filteredFavorites.map(card => (
                      <PokemonCardItem
                        key={card.id}
                        card={card}
                        onClick={() => handleOpenCard(card, filteredFavorites)}
                        onQuickAdd={toggleFavorite}
                        isFavorite={true}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Wishlist Matches Alert - Cards You Have That Others Want */}
            {wishlistMatches.length > 0 && (
              <div className="mt-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-yellow-500/20 rounded-xl">
                    <Zap className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-yellow-400 uppercase tracking-widest">Trade Alert</h3>
                    <p className="text-xs text-slate-400">{wishlistMatches.length} of your cards are wanted by other collectors</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {wishlistMatches.slice(0, 6).map(match => (
                    <div
                      key={match.card.id}
                      onClick={() => handleOpenCard(match.card, favorites)}
                      className="group relative cursor-pointer"
                    >
                      <img
                        src={match.card.images.small}
                        alt={match.card.name}
                        className="w-16 h-auto rounded-lg border-2 border-yellow-500/50 shadow-lg shadow-yellow-500/20 group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded-full">
                        {match.wantedBy.length}
                      </div>
                    </div>
                  ))}
                  {wishlistMatches.length > 6 && (
                    <div className="w-16 h-20 bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-center">
                      <span className="text-xs font-bold text-slate-400">+{wishlistMatches.length - 6}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Wishlist Section */}
            <div className="mt-10 pt-8 border-t border-slate-800/50">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  <h2 className="text-xl font-black text-white uppercase tracking-tighter">Wishlist</h2>
                  <span className="bg-slate-800 px-3 py-1 rounded-full text-xs font-bold text-slate-400">{wishlist.length}</span>
                </div>
              </div>

              {wishlist.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 border border-dashed border-slate-800 rounded-2xl">
                  <Star className="w-12 h-12 text-slate-700 mb-4" />
                  <h3 className="text-md font-bold text-slate-600 uppercase">No Cards on Wishlist</h3>
                  <p className="text-slate-500 mt-1 text-xs">Tap the star on any card to add it to your wishlist</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {wishlist.map(item => (
                    <div
                      key={item.cardId}
                      className="group relative cursor-pointer"
                      onClick={() => handleOpenWishlistCard(item.cardId)}
                    >
                      <img
                        src={item.cardImage}
                        alt={item.cardName}
                        className="w-full h-auto rounded-xl border border-slate-700 shadow-lg group-hover:scale-105 group-hover:border-yellow-500/50 transition-all"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setWishlist(prev => prev.filter(w => w.cardId !== item.cardId));
                        }}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove from Wishlist"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 rounded-b-xl">
                        <p className="text-[9px] font-bold text-white truncate">{item.cardName}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'decks' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Tactical Formations</h2>
              <button
                onClick={() => setShowCreateDeckModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors shadow-lg shadow-blue-600/20"
              >
                <Plus className="w-4 h-4" /> New Formation
              </button>
            </div>

            {decks.length === 0 ? (
              <div className="text-center py-20 bg-slate-900/50 rounded-3xl border border-slate-800">
                <Library className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500 font-bold uppercase tracking-widest">No Formations Established</p>
                <p className="text-slate-600 text-xs mt-2">Initialize a new formation above or add from Card Details.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {decks.map(deck => (
                  <div key={deck.id} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 relative group hover:border-blue-500/30 transition-colors shadow-xl">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        {editingDeckId === deck.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              autoFocus
                              type="text"
                              title="Formation Name"
                              aria-label="Rename Formation"
                              className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                              defaultValue={deck.name}
                              onBlur={(e) => updateDeckName(deck.id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') updateDeckName(deck.id, e.currentTarget.value);
                                if (e.key === 'Escape') setEditingDeckId(null);
                              }}
                            />
                            <button onClick={() => setEditingDeckId(null)} className="p-1 text-slate-500 hover:text-white" aria-label="Cancel Rename">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group/title">
                            <h3 className="text-xl font-black text-white uppercase tracking-tight">{deck.name}</h3>
                            <button
                              onClick={() => setEditingDeckId(deck.id)}
                              className="opacity-0 group-hover/title:opacity-100 p-1 text-slate-500 hover:text-blue-400 transition-all"
                              aria-label="Edit Name"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{new Date(deck.createdAt).toLocaleDateString()}</p>
                          <span className="text-xs text-slate-700 font-black">•</span>
                          <p className="text-xs text-green-500 font-black uppercase tracking-widest font-mono">
                            ${getDeckValue(deck).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeDeck(deck.id)}
                        className="p-2 text-slate-600 hover:text-red-500 transition-colors bg-slate-950 rounded-xl border border-slate-800"
                        aria-label="Delete Formation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {deck.cards.length === 0 ? (
                      <div className="w-full h-32 bg-slate-950/30 rounded-xl border-2 border-dashed border-slate-800 flex flex-col items-center justify-center mb-4 group-hover:border-slate-700 transition-colors">
                        <Plus className="w-6 h-6 text-slate-700 mb-2" />
                        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Empty Formation</span>
                      </div>
                    ) : (
                      <div className="flex -space-x-4 overflow-hidden py-4 pl-2 mb-2 h-32 items-center">
                        {deck.cards.slice(0, 5).map((c, i) => (
                          <img key={i} src={c.images.small} alt="" className="w-20 h-28 object-contain rounded-lg border-2 border-slate-900 shadow-2xl transform rotate-3 bg-slate-800" />
                        ))}
                        {deck.cards.length > 5 && (
                          <div className="w-20 h-28 bg-slate-800 rounded-lg border-2 border-slate-900 flex items-center justify-center text-xs font-bold text-slate-400 z-10 transform rotate-3 shadow-2xl shrink-0">
                            +{deck.cards.length - 5}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{deck.cards.length} Units</span>
                      <button
                        onClick={() => {
                          const first = deck.cards[0];
                          if (first) handleOpenCard(first, deck.cards, deck.id);
                        }}
                        disabled={deck.cards.length === 0}
                        className="text-blue-400 disabled:text-slate-700 disabled:cursor-not-allowed text-xs font-black uppercase tracking-widest hover:text-white flex items-center gap-1 group/btn"
                      >
                        Inspect <ChevronRight className="w-3 h-3 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'social' && (
          <Suspense fallback={<div className="flex justify-center p-20"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div>}>
            <SocialHub
              isPro={isPro}
              onTriggerPaywall={() => setShowPaywall(true)}
              onViewCard={(c) => handleOpenCard(c, [])} // Context empty for social view for now
              onQuickAdd={toggleFavorite}
              favorites={favorites}
              cachedProfiles={socialProfiles}
              onProfilesLoaded={setSocialProfiles}
              user={user}
              onUpdateProfile={handleUpdateProfile} // Passing the update handler
            />
          </Suspense>
        )}

        {activeTab === 'messages' && user && (
          <Suspense fallback={<div className="flex justify-center p-20"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div>}>
            <MessageCenter
              user={user}
              onOpenChat={(profile) => setActiveChatRecipient(profile)}
              lastUpdate={latestNotification?.timestamp}
            />
          </Suspense>
        )}

        {activeTab === 'auctions' && user && (
          <Suspense fallback={<div className="flex justify-center p-20"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div>}>
            <AuctionHouse
              user={user}
              subscriptionTier={subscriptionTier}
              onViewCard={(card) => handleOpenCard(card, [])}
            />
          </Suspense>
        )}
      </main>

      {/* MOBILE BOTTOM NAV */}
      <BottomNav
        activeTab={activeTab === 'favorites' || activeTab === 'decks' ? 'profile' : activeTab} // Simple mapping, profile tab handles 'more'
        onTabChange={(tab) => {
          // Handle the 'profile' tab click as a menu toggle in UI, but if clicked, default to profile view or toggle menu
          setActiveTab(tab);
        }}
        onScan={() => setShowScanner(true)}
        onProfileClick={() => setShowProfileMenu(true)} // Profile tab on bottom nav opens the menu
        unreadCount={unreadCount}
        user={user}
      />

      {selectedCard && (
        <CardDetailsModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onToggleFavorite={toggleFavorite}
          isFavorite={favorites.some(f => f.id === selectedCard.id)}
          onToggleWishlist={toggleWishlist}
          isOnWishlist={isOnWishlist(selectedCard.id)}
          onUpdateQuantity={updateQuantity}
          onUpdateCondition={updateCondition}
          decks={decks}
          onAddToDeck={addToDeck}
          onCreateDeckAndAdd={createDeckAndAdd}
          isAuthenticated={isAuthenticated}
          onNavigate={handleNavigateCard}
          onSelectCard={(c) => handleOpenCard(c, [], currentDeckId || undefined)} // Allow jumping to related cards
          onSelectSet={handleSelectSet}
          currentDeckId={currentDeckId}
          onRemoveFromDeck={removeFromDeck}
        />
      )}

      {showScanner && (
        <Suspense fallback={<div className="fixed inset-0 z-50 bg-black flex items-center justify-center"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" /></div>}>
          <CameraScanner
            onIdentify={handleScanIdentify}
            onClose={() => setShowScanner(false)}
            decks={decks}
          />
        </Suspense>
      )}

      {showPaywall && (
        <Suspense fallback={null}>
          <SubscriptionModal
            onClose={() => setShowPaywall(false)}
            onSubscribe={handleSubscribe}
            onCancel={handleCancelSubscription}
            user={user}
            currentTier={subscriptionTier}
          />
        </Suspense>
      )}

      {activeChatRecipient && user && (
        <Suspense fallback={null}>
          <ChatWindow
            recipient={activeChatRecipient}
            currentUser={user}
            onClose={() => setActiveChatRecipient(null)}
          />
        </Suspense>
      )}

      {showCreateDeckModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-black text-white uppercase">Initialize Formation</h3>
              <button
                onClick={() => setShowCreateDeckModal(false)}
                className="text-slate-500 hover:text-white"
                aria-label="Close Creation Modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <input aria-label="Deck Name"
              autoFocus
              type="text"
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              placeholder="Enter formation codename..."
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white mb-4 focus:border-blue-500 outline-none font-bold"
              onKeyDown={(e) => e.key === 'Enter' && createDeck(newDeckName)}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowCreateDeckModal(false)} className="flex-1 py-3 bg-slate-800 text-slate-300 rounded-xl font-bold uppercase text-xs hover:bg-slate-700">Cancel</button>
              <button
                onClick={() => createDeck(newDeckName)}
                disabled={!newDeckName.trim()}
                className="flex-1 py-3 bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-black uppercase text-xs hover:bg-blue-500 shadow-lg shadow-blue-600/20"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        {showOnboarding && <OnboardingModal onClose={handleFinishOnboarding} />}
        {showChangelog && <ChangelogModal update={systemService.getLatestUpdate()} onClose={handleCloseChangelog} />}
        {showMission && <MissionModal onClose={() => setShowMission(false)} />}
      </Suspense>

    </div>
  );
};

export default App;
